import { CaretDown, CaretUp } from "@phosphor-icons/react";
import { useState } from "react";
import type { StepStatus } from "../../lib/types.js";
import { JsonTreeView } from "./JsonTreeView.js";

interface StepInspectPanelProps {
	step: StepStatus;
	stepType: string;
	onApprove: (approvalKey: string, approved: boolean, reason?: string) => void;
}

const STATUS_COLORS: Record<string, string> = {
	pending: "var(--forge-color-text-muted)",
	running: "var(--forge-color-accent)",
	completed: "var(--forge-color-success)",
	failed: "var(--forge-color-error)",
	skipped: "var(--forge-color-text-muted)",
};

function formatDuration(ms: number): string {
	if (ms < 1000) return `${ms}ms`;
	return `${(ms / 1000).toFixed(1)}s`;
}

function formatTimestamp(ts: string | null): string {
	if (!ts) return "—";
	return new Date(ts).toLocaleTimeString();
}

interface CollapsibleSectionProps {
	label: string;
	children: React.ReactNode;
	defaultOpen?: boolean;
}

function CollapsibleSection({ label, children, defaultOpen = false }: CollapsibleSectionProps) {
	const [open, setOpen] = useState(defaultOpen);
	return (
		<div className="border-t border-border pt-3">
			<button
				type="button"
				onClick={() => setOpen((v) => !v)}
				className="mb-2 flex w-full items-center justify-between text-xs font-semibold uppercase tracking-widest text-text-muted"
			>
				<span>{label}</span>
				<span>{open ? <CaretUp size={12} /> : <CaretDown size={12} />}</span>
			</button>
			{open && <div>{children}</div>}
		</div>
	);
}

export function StepInspectPanel({ step, stepType, onApprove }: StepInspectPanelProps) {
	const [reason, setReason] = useState("");

	const showRetries = step.attempt > 1 || step.status === "failed";
	const showApproval = !!step.approvalKey && step.status === "running";

	return (
		<div className="flex h-full flex-col overflow-y-auto p-4">
			{/* Header */}
			<div className="mb-4">
				<div className="mb-1 text-xs font-semibold uppercase tracking-widest text-text-muted">
					Step Inspector
				</div>
				<div className="mb-1 text-sm font-semibold text-text-primary">{step.stepId}</div>
				<div className="mb-2 text-xs text-text-muted">{stepType}</div>
				<span
					className="inline-block rounded-full px-2 py-0.5 text-xs font-semibold text-white"
					style={{ backgroundColor: STATUS_COLORS[step.status] ?? "var(--forge-color-text-muted)" }}
					data-testid="status-badge"
				>
					{step.status}
				</span>
			</div>

			{/* Timing */}
			<div className="mb-3 border-t border-border pt-3">
				<div className="mb-1 text-xs font-semibold uppercase tracking-widest text-text-muted">
					Timing
				</div>
				<div className="grid grid-cols-2 gap-y-1">
					<span className="text-xs text-text-muted">Started</span>
					<span className="text-xs text-text-primary">{formatTimestamp(step.startedAt)}</span>
					<span className="text-xs text-text-muted">Completed</span>
					<span className="text-xs text-text-primary">{formatTimestamp(step.completedAt)}</span>
					{step.duration !== null && (
						<>
							<span className="text-xs text-text-muted">Duration</span>
							<span className="text-xs text-text-primary" data-testid="duration">
								{formatDuration(step.duration)}
							</span>
						</>
					)}
				</div>
			</div>

			{/* Retries */}
			{showRetries && (
				<div className="mb-3 border-t border-border pt-3">
					<div className="text-xs font-semibold uppercase tracking-widest text-text-muted">
						Retries
					</div>
					<div className="mt-1 text-xs text-text-primary" data-testid="retry-count">
						Attempt {step.attempt} of {step.maxAttempts}
					</div>
				</div>
			)}

			{/* Error */}
			{step.error && (
				<div
					className="mb-3 rounded-md border border-error/30 bg-error/10 p-3"
					data-testid="error-box"
				>
					<div className="mb-1 text-xs font-semibold uppercase tracking-widest text-error">
						Error
					</div>
					<div className="text-xs text-error">{step.error}</div>
				</div>
			)}

			{/* Input */}
			<CollapsibleSection label="Input">
				<JsonTreeView data={step.input} defaultExpandDepth={2} />
			</CollapsibleSection>

			{/* Output */}
			<CollapsibleSection label="Output">
				<JsonTreeView data={step.output} defaultExpandDepth={2} />
			</CollapsibleSection>

			{/* Approval controls */}
			{showApproval && (
				<div className="mt-3 border-t border-border pt-3" data-testid="approval-controls">
					<div className="mb-2 text-xs font-semibold uppercase tracking-widest text-text-muted">
						Approval Required
					</div>
					<textarea
						value={reason}
						onChange={(e) => setReason(e.target.value)}
						placeholder="Optional reason…"
						rows={2}
						className="mb-2 block w-full rounded-md border border-border bg-canvas px-3 py-2 text-xs text-text-primary placeholder:text-text-muted/50 focus:border-accent focus:ring-1 focus:ring-accent focus:outline-none"
						data-testid="reason-input"
					/>
					<div className="flex gap-2">
						<button
							type="button"
							onClick={() => onApprove(step.approvalKey ?? "", true, reason || undefined)}
							className="flex-1 rounded-xl bg-success px-3 py-2 text-xs font-semibold text-white transition-opacity hover:opacity-90"
							data-testid="approve-button"
						>
							Approve
						</button>
						<button
							type="button"
							onClick={() => onApprove(step.approvalKey ?? "", false, reason || undefined)}
							className="flex-1 rounded-xl bg-error px-3 py-2 text-xs font-semibold text-white transition-opacity hover:opacity-90"
							data-testid="reject-button"
						>
							Reject
						</button>
					</div>
				</div>
			)}
		</div>
	);
}
